import { Button, TextField } from '@material-ui/core';
import {
  MANAGEMENT_TIMELINE_GROUP_BY_VALUES,
  MANAGEMENT_TIMELINE_MARKER_TYPE_VALUES,
  MANAGEMENT_TIMELINE_OUTCOME_VALUES,
  type ManagementTimelineQuery,
} from '@internal/spassu-code-contract';
import { useEffect, useState } from 'react';
import {
  timelineGroupByLabel,
  timelineOutcomeLabel,
  timelineTypeLabel,
} from '../model/semanticLabels';
import { useCockpitStyles } from './cockpitStyles';
import {
  applyTimelineFilterDraft,
  clearTimelineFilters,
  toTimelineFilterDraft,
  type TimelineFilterDraft,
} from './timelineFilterState';

const TYPE_OPTIONS = MANAGEMENT_TIMELINE_MARKER_TYPE_VALUES.map(value => ({
  value,
  label: timelineTypeLabel(value),
}));
const OUTCOME_OPTIONS = MANAGEMENT_TIMELINE_OUTCOME_VALUES.map(value => ({
  value,
  label: timelineOutcomeLabel(value),
}));
const ATTENTION_OPTIONS = [
  { value: 'true', label: 'Requer atenção' },
  { value: 'false', label: 'Sem atenção requerida' },
];
const GROUP_OPTIONS = MANAGEMENT_TIMELINE_GROUP_BY_VALUES.map(value => ({
  value,
  label: timelineGroupByLabel(value),
}));

export const TimelineFilters = ({
  query,
  onChange,
}: {
  query: ManagementTimelineQuery;
  onChange(query: ManagementTimelineQuery): void;
}) => {
  const classes = useCockpitStyles();
  const [draft, setDraft] = useState(() => toTimelineFilterDraft(query));
  useEffect(() => setDraft(toTimelineFilterDraft(query)), [query]);
  const update = (key: keyof TimelineFilterDraft, value: string) =>
    setDraft(current => ({ ...current, [key]: value }));
  return (
    <form
      aria-label="Filtros da timeline"
      onSubmit={event => {
        event.preventDefault();
        onChange(applyTimelineFilterDraft(query, draft));
      }}
    >
      <div className={classes.timelineFilters}>
        <TimelineFilterInputs draft={draft} onChange={update} />
        <TimelineFilterActions
          onClear={() => onChange(clearTimelineFilters(query))}
        />
      </div>
    </form>
  );
};

function TimelineFilterInputs({
  draft,
  onChange,
}: {
  draft: TimelineFilterDraft;
  onChange(key: keyof TimelineFilterDraft, value: string): void;
}) {
  return (
    <>
      <SelectFilter
        id="type"
        label="Tipo"
        value={draft.type}
        options={TYPE_OPTIONS}
        onChange={onChange}
      />
      <SelectFilter
        id="outcome"
        label="Resultado"
        value={draft.outcome}
        options={OUTCOME_OPTIONS}
        onChange={onChange}
      />
      <TextField
        id="timeline-skill"
        label="Skill"
        value={draft.skill}
        onChange={event => onChange('skill', event.target.value)}
      />
      <DateFilter id="from" label="De" value={draft.from} onChange={onChange} />
      <DateFilter id="to" label="Até" value={draft.to} onChange={onChange} />
      <SelectFilter
        id="attention"
        label="Atenção"
        value={draft.attention}
        options={ATTENTION_OPTIONS}
        onChange={onChange}
      />
      <SelectFilter
        id="groupBy"
        label="Agrupar por"
        value={draft.groupBy}
        options={GROUP_OPTIONS}
        emptyLabel="Sem dimensão adicional"
        onChange={onChange}
      />
    </>
  );
}

function SelectFilter({
  id,
  label,
  value,
  options,
  emptyLabel = 'Todos',
  onChange,
}: {
  id: keyof TimelineFilterDraft;
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  emptyLabel?: string;
  onChange(key: keyof TimelineFilterDraft, value: string): void;
}) {
  return (
    <TextField
      id={`timeline-${id}`}
      select
      label={label}
      value={value}
      SelectProps={{ native: true }}
      // a native select always shows an option, so the label has nowhere to
      // rest: without this it sits on top of the current value
      InputLabelProps={{ shrink: true }}
      onChange={event => onChange(id, event.target.value)}
    >
      <option value="">{emptyLabel}</option>
      {options.map(option => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </TextField>
  );
}

function DateFilter({
  id,
  label,
  value,
  onChange,
}: {
  id: 'from' | 'to';
  label: string;
  value: string;
  onChange(key: keyof TimelineFilterDraft, value: string): void;
}) {
  return (
    <TextField
      id={`timeline-${id}`}
      label={label}
      type="datetime-local"
      value={value}
      InputLabelProps={{ shrink: true }}
      onChange={event => onChange(id, event.target.value)}
    />
  );
}

function TimelineFilterActions({ onClear }: { onClear(): void }) {
  const classes = useCockpitStyles();
  return (
    <div className={classes.timelineFilterActions}>
      <Button type="submit" variant="contained" color="primary">
        Aplicar filtros
      </Button>
      <Button type="button" onClick={onClear}>
        Limpar filtros
      </Button>
    </div>
  );
}
